import { CourseMetadataKeys } from "../../constants/courses.const";
import { CourseSettingsSourceEnum, TimeZoneEnum } from "../../enums";
import {
  ICourseDto,
  ICourseMetadata,
  ICourseStationInfo,
  ILocationInfo,
  IRideRoute,
} from "../../models";
import { isValidTimeString, timeDifferenceSameDate } from "../date.helpers";
import { isStationWithValidMapCoordinates } from "../map.helpers";
import {
  isRoutingWaypointDragOrderable,
  isStationRouteWaypointStation,
} from "../station-route";

const MIN_COURSE_STATIONS_FOR_DURATIONS_COUNT = 2;

export const getCourseStationById = (
  courseDto: ICourseDto,
  stationId: number | undefined,
): ICourseStationInfo | undefined => {
  if (!stationId || !courseDto?.metadata?.stationsInfo) {
    return undefined;
  }

  return courseDto.metadata.stationsInfo.find(x => x.id === stationId);
};

export const getCourseStationDuration = (
  courseDto: ICourseDto | undefined,
  index: number,
  timezone: TimeZoneEnum,
): number | undefined => {
  const stations = courseDto?.metadata?.stationsInfo;

  if (!stations?.length || index >= stations.length) {
    return undefined;
  }

  const timeA = stations[index]?.timeArrival;
  const timeB = stations[index + 1]?.timeArrival;

  if (
    !timeA ||
    !timeB ||
    !isValidTimeString(timeA) ||
    !isValidTimeString(timeB)
  ) {
    return undefined;
  }

  return timeDifferenceSameDate(timeA, timeB, "minute", timezone);
};

export const getNewCourseTemplate = (
  createdAt: TDateISO,
): Partial<ICourseDto> => {
  const newCourse: Partial<ICourseDto> = {
    createdAt,
    metadata: {
      stationIds: [],
      stationsInfo: [],
      settingsSource: CourseSettingsSourceEnum.ManualForm,
    },
  };
  return newCourse;
};

export const getCourseRoute = (
  course?: Partial<ICourseDto>,
): ILocationInfo[] => {
  return (
    course?.metadata?.stationsInfo
      ?.filter(isStationWithValidMapCoordinates)
      .map(stationInfo => {
        const isRoutingWaypoint = Boolean(stationInfo.waypointCategory);
        const location: ILocationInfo = {
          ...stationInfo.locationInfo,
          addressName: stationInfo.name,
          fullAddress:
            stationInfo.name || stationInfo.locationInfo?.fullAddress,
          waypointCategory: stationInfo.waypointCategory,
          stationId: isRoutingWaypoint ? undefined : stationInfo.id,
        };
        return location;
      }) ?? []
  );
};

export const getCourseRouteInfo = (
  courseDto: Partial<ICourseDto>,
): IRideRoute => {
  return {
    rideId: 0,
    locations: getCourseRoute(courseDto),
    routePolyline: courseDto?.metadata?.[CourseMetadataKeys.routePolyline],
    courseId: courseDto?.id,
    direction: courseDto?.metadata?.direction,
    isManualPolylineEdit:
      courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit],
  };
};

export const getCourseRoutesInfo = (
  courseDto?: Partial<ICourseDto>,
): IRideRoute[] => {
  return courseDto ? [getCourseRouteInfo(courseDto)] : [];
};

/** Clears stored route geometry after a structural edit; keeps it when the user drew the polyline manually. */
export const clearCourseRouteGeometryUnlessManualPolyline = ({
  metadata,
  doNotAutoUpdateCourses = false,
}: {
  metadata: ICourseMetadata;
  doNotAutoUpdateCourses?: boolean;
}): ICourseMetadata => {
  if (metadata[CourseMetadataKeys.isManualPolylineEdit]) {
    return metadata;
  }

  return {
    ...metadata,
    [CourseMetadataKeys.routePolyline]: undefined,
    ...(doNotAutoUpdateCourses
      ? {}
      : { distances: undefined, durations: undefined }),
  };
};

export const mergeInsertedCourseStations = (
  stations: ICourseStationInfo[],
  stationsAdd: ICourseStationInfo[],
): ICourseStationInfo[] => {
  if (!stationsAdd?.length) {
    return stations;
  }

  if (stations.length > 1) {
    const [firstStation, ...restStations] = stations;
    return [firstStation, ...stationsAdd, ...restStations];
  }

  if (stations.length === 1) {
    return [...stationsAdd, ...stations];
  }

  return [...stationsAdd];
};

export const getCourseDurationMin = (
  courseDto: Partial<ICourseDto> | undefined,
  timezone: TimeZoneEnum,
): number => {
  if (!courseDto) {
    return 0;
  }

  const courseStations = courseDto?.metadata?.stationsInfo;

  if (
    !courseStations?.length ||
    courseStations?.length < MIN_COURSE_STATIONS_FOR_DURATIONS_COUNT
  ) {
    return 0;
  }

  const firstStation = courseStations.at(0);
  const lastStation = courseStations.at(-1);

  if (
    !firstStation?.timeArrival ||
    !lastStation?.timeArrival ||
    !isValidTimeString(firstStation.timeArrival) ||
    !isValidTimeString(lastStation.timeArrival)
  ) {
    return 0;
  }

  const time = timeDifferenceSameDate(
    firstStation.timeArrival,
    lastStation.timeArrival,
    "minute",
    timezone,
  );

  return time ?? 0;
};

export const isCourseStationDragOrderable = ({
  station,
  isFirstStation,
  isLastStation,
  isSingleStation,
  stations,
}: {
  station?: ICourseStationInfo;
  isFirstStation?: boolean;
  isLastStation?: boolean;
  isSingleStation?: boolean;
  stations?: ICourseStationInfo[];
}): boolean => {
  if (station?.isLocked || isSingleStation) {
    return false;
  }

  if (!isStationRouteWaypointStation(station)) {
    return true;
  }

  return isRoutingWaypointDragOrderable({
    station,
    isLocked: station?.isLocked,
    isFirstStation,
    isLastStation,
    stations,
  });
};
